import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { showBasicAlert } from "../../../../core/common/useAlert";
import api from "../../../../services/api";
import ComposeEmail from "./ComposeEmail";
import EmailDetail from "./EmailDetail";
import "../../../../style/css/email.css";
import Loader from "../../../../core/common/loader";

export interface Email {
  uid: number;
  from: string;
  to: string;
  cc: string;
  subject: string;
  text: string;
  date: string;
  messageId: string;
  inReplyTo: string | null;
  references: string[];
  isReply: boolean;
  attachments: any[];
  profileImage?: string;
  isRead?: boolean;
  isDraft?: boolean;
}

type ComposeReplyEmail = {
  from: string;
  to: string;
  subject: string;
  messageId: string;
  references: string[];
  isReply: boolean;
};

interface EmailData {
  to: string[];
  cc?: string[];
  subject: string;
  message: string;
  inReplyTo?: string;
  references?: string[];
  replyAll?: boolean;
  attachments?: File[];
}

interface HostingerMailViewProps {
  provider: string;
}

const HostingerMailView: React.FC<HostingerMailViewProps> = ({ provider }) => {
  const [showSelectionMode, setShowSelectionMode] = useState<boolean>(false);
  const [refreshEmail, setrefreshEmail] = useState<boolean>(false);
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);

  const [showSearch, setShowSearch] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [emails, setEmails] = useState<Email[]>([]);
  const [sentEmails, setSentEmails] = useState<Email[]>([]);
  const [draftEmails, setDraftEmails] = useState<Email[]>([]);
  const [trashEmails, setTrashEmails] = useState<Email[]>([]);
  const [junkEmails, setJunkEmails] = useState<Email[]>([]);
  const [filteredEmails, setFilteredEmails] = useState<Email[]>([]);
  const [filteredSentEmails, setFilteredSentEmails] = useState<Email[]>([]);
  const [filteredDraftEmails, setFilteredDraftEmails] = useState<Email[]>([]);
  const [filteredTrashEmails, setFilteredTrashEmails] = useState<Email[]>([]);
  const [filteredJunkEmails, setFilteredJunkEmails] = useState<Email[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [selectedEmails, setSelectedEmails] = useState<number[]>([]);
  const [isReplying, setIsReplying] = useState(false);
  const [isForwarding, setIsForwarding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingLogout, setIsLoadingLogout] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNewEmail, setNewEmail] = useState(0);
  const [activeTab, setActiveTab] = useState<
    "inbox" | "sent" | "drafts" | "trash" | "junk"
  >("inbox");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const emailsPerPage = 50;

  const signature = localStorage.getItem("signature");

  const sortEmailsByDate = (emails: Email[]) =>
    [...emails].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );

  const filterEmails = (emails: Email[], query: string) => {
    if (!query) return emails;
    const lower = query.toLowerCase();
    return emails.filter(
      (e) =>
        e.from.toLowerCase().includes(lower) ||
        e.to.toLowerCase().includes(lower) ||
        e.subject.toLowerCase().includes(lower) ||
        e.text.toLowerCase().includes(lower)
    );
  };

  const fetchEmails = async () => {
    if (!signature) {
      showBasicAlert("Authentication signature missing.");
      return;
    }
    setIsLoading(refreshEmail == true ? false : true);
    try {
      const [inboxRes, sentRes, draftRes, trashRes, junkRes] =
        await Promise.all([
          api.post("mailService/receiveMailBox", { signature }),
          api.post("mailService/getSendMailBox", { signature }),
          api.post("mailService/getDraftMailBox", { signature }),
          api.post("mailService/getTrashMailBox", { signature }),
          api.post("mailService/getJunkMailBox", { signature }),
        ]);

      const inboxData = sortEmailsByDate(inboxRes.data.message);
      const sentData = sortEmailsByDate(sentRes.data.message);
      const draftData = sortEmailsByDate(draftRes.data.message);
      const trashData = sortEmailsByDate(trashRes.data.message);
      const junkData = sortEmailsByDate(junkRes.data.message);

      setEmails(inboxData);
      setSentEmails(sentData);
      setDraftEmails(draftData);
      setTrashEmails(trashData);
      setJunkEmails(junkData);

      setFilteredEmails(inboxData);
      setFilteredSentEmails(sentData);
      setFilteredDraftEmails(draftData);
      setFilteredTrashEmails(trashData);
      setFilteredJunkEmails(junkData);

      setUnreadCount(inboxData.filter((e) => !e.isRead).length);

      if (isNewEmail == 0) {
        setNewEmail(inboxData.filter((e) => !e.isRead).length);
      } else {
        if (inboxData.filter((e) => !e.isRead).length > isNewEmail) {
          const audio = new Audio("/sounds/received_mail.mp3");
          audio.volume = 1.0; // Set volume to maximum
          audio.play().catch((e) => console.warn("Audio play failed:", e));
        }
        setNewEmail(inboxData.filter((e) => !e.isRead).length);
      }
    } catch (e) {
      console.error("Error fetching emails:", e);
    } finally {
      setrefreshEmail(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Call once immediately
    fetchEmails();

    // Set interval to run every 30 seconds (30000ms)
    const intervalId = setInterval(() => {
      setrefreshEmail(true);
    }, 30000);

    // Cleanup interval when component unmounts
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (refreshEmail) {
      fetchEmails();
    }
  }, [refreshEmail]);

  useEffect(() => {
    const filterTab = () => {
      setFilteredEmails(filterEmails(emails, searchQuery));
      setFilteredSentEmails(filterEmails(sentEmails, searchQuery));
      setFilteredDraftEmails(filterEmails(draftEmails, searchQuery));
      setFilteredTrashEmails(filterEmails(trashEmails, searchQuery));
      setFilteredJunkEmails(filterEmails(junkEmails, searchQuery));
    };
    filterTab();
  }, [searchQuery, emails, sentEmails, draftEmails, trashEmails, junkEmails]);

  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab);
    setSelectedEmail(null);
    setSelectedEmails([]);
    setSearchQuery("");
    setCurrentPage(1);
  };

  const getVisibleEmails = () => {
    const data =
      activeTab === "inbox"
        ? filteredEmails
        : activeTab === "sent"
        ? filteredSentEmails
        : activeTab === "drafts"
        ? filteredDraftEmails
        : activeTab === "trash"
        ? filteredTrashEmails
        : filteredJunkEmails;
    const start = (currentPage - 1) * emailsPerPage;
    return data.slice(start, start + emailsPerPage);
  };

  const totalPages = () => {
    const data =
      activeTab === "inbox"
        ? filteredEmails
        : activeTab === "sent"
        ? filteredSentEmails
        : activeTab === "drafts"
        ? filteredDraftEmails
        : activeTab === "trash"
        ? filteredTrashEmails
        : filteredJunkEmails;
    return Math.ceil(data.length / emailsPerPage);
  };

  const handleComposeSubmit = async (data: FormData | EmailData) => {
    if (!signature) {
      showBasicAlert(
        "Authentication signature is missing. Please log in again."
      );
      return;
    }

    setIsLoading(true);
    try {
      const formData = data instanceof FormData ? data : new FormData();

      if (!(data instanceof FormData)) {
        let message = data.message;
        let subject = data.subject;

        if (isForwarding && selectedEmail) {
          subject = `Fwd: ${selectedEmail.subject}`;
          message =
            `---------- Forwarded message ----------\n` +
            `From: ${selectedEmail.from}\n` +
            `Date: ${selectedEmail.date}\n` +
            `Subject: ${selectedEmail.subject}\n\n` +
            `${data.message}\n\n` +
            `${selectedEmail.text}`;
        }
        const toArray = Array.isArray(data.to) ? data.to : [data.to];
        const ccArray = Array.isArray(data.cc)
          ? data.cc
          : data.cc
          ? [data.cc]
          : [];

        formData.set("to", JSON.stringify(toArray));
        formData.set("cc", JSON.stringify(ccArray));

        formData.append("subject", subject);
        formData.append("message", message);

        if (data.inReplyTo) {
          formData.append("inReplyTo", data.inReplyTo);
        }
        if (data.references) {
          formData.append("references", JSON.stringify(data.references));
        }
        if (data.replyAll !== undefined) {
          formData.append("replyAll", data.replyAll.toString());
        }
        if (data.attachments) {
          data.attachments.forEach((file) => {
            formData.append("attachments", file);
          });
        }
      }

      if (activeTab === "drafts" && selectedEmail?.uid) {
        formData.append("draftUid", selectedEmail.uid.toString());
      }
      formData.append("signature", signature);

      const endpoint =
        activeTab === "drafts" && selectedEmail?.uid
          ? "mailService/sendDraftHandler"
          : "mailService/sendEmailHandler";

      await api.post(endpoint, formData);

      setShowCompose(false);
      setIsForwarding(false);
      fetchEmails();
      showBasicAlert("Email sent successfully!");
    } catch (error) {
      console.error("Error sending email:", error);
      showBasicAlert("Failed to send email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const convertToComposeReplyEmail = (email: Email): ComposeReplyEmail => ({
    from: email.from,
    to: email.to,
    subject: email.subject,
    messageId: email.messageId,
    references: email.references || [],
    isReply: email.isReply || false,
  });

  const toggleEmailSelection = (uid: number) => {
    setSelectedEmails((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  const handleEmailSelection = (email: Email) => {
    if (showSelectionMode) {
      toggleEmailSelection(email.uid);
      return;
    }

    // If it's a draft, open in compose mode
    if (activeTab === "drafts") {
      setSelectedEmail({
        ...email,
        isDraft: true,
        attachments: email.attachments || [],
      });
      setShowCompose(true);
      return;
    }

    // Regular email handling
    setSelectedEmail(email);
    if (activeTab === "inbox") {
      markEmailAsRead(email);
    }
  };

  const renderEmailList = () => {
    const list = getVisibleEmails();
    return (
      <>
        <div className="gmail-pagination">
          <span className="range">
            Page {currentPage} of {totalPages()}
          </span>
          <button
            className="arrow"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            &#x276E; {/* Left Arrow */}
          </button>
          <button
            className="arrow"
            disabled={currentPage === totalPages()}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            &#x276F; {/* Right Arrow */}
          </button>
        </div>

        {list.map((email) => (
          <div
            className={`gmail-email-item ${
              email.isRead ? "read" : "unread"
            } fade-in`}
            key={email.uid}
            onClick={() => {
              setSelectedEmail(email);
              handleEmailSelection(email);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "10px 12px",
              borderBottom: "1px solid #f1f3f4",
              cursor: "pointer",
            }}
          >
            {/* Avatar */}
            <div className="avatar me-3">
              <ImageWithBasePath
                src={email.profileImage || "assets/img/profiles/avatar-01.jpg"}
                alt="avatar"
                className="rounded-circle"
              />
            </div>

            {/* Middle Content (Sender + Subject + Snippet) */}
            <div
              className="email-main flex-grow-1"
              style={{
                display: "flex",
                flexDirection: "column",
                minWidth: 0, // Important for ellipsis to work
              }}
            >
              {/* Sender */}
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  color: "#202124",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {activeTab === "inbox"
                  ? email.from.split("<")[0].replace(/"/g, "").trim()
                  : email.to.split("<")[0].replace(/"/g, "").trim()}
              </div>

              {/* Subject + Snippet in One Line */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "0.9rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  gap: "4px",
                }}
              >
                <span style={{ fontWeight: 600, color: "#202124" }}>
                  {email.subject.substring(0, 100) || "(No Subject)"}
                </span>
                <span
                  style={{
                    color: "#5f6368",
                    flexShrink: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  -- {email.text.substring(0, 30) || ""}
                </span>
              </div>
            </div>

            {/* Right Section (Date & Delete) */}
            <div
              className="email-meta"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                marginLeft: "10px",
                flexShrink: 0, // Never shrink time/buttons
              }}
            >
              <span
                style={{
                  fontSize: "0.8rem",
                  color: "#5f6368",
                  textAlign: "right",
                  lineHeight: "1.2",
                }}
              >
                {moment(email.date).format("DD MMM YYYY")}
                <br />
                {moment(email.date).format("hh:mm A")}
              </span>
              <button
                className="btn btn-sm mt-1"
                style={{
                  color: "#d93025",
                  border: "none",
                  background: "transparent",
                }}
                title="Delete Email"
                onClick={(e) => {
                  e.stopPropagation();
                  moveToBin([email.uid]);
                }}
              >
                <i className="ti ti-trash fs-18" />
              </button>
            </div>
          </div>
        ))}
      </>
    );
  };

  const markEmailAsRead = async (email: Email) => {
    if (!signature || email.isRead) return;

    try {
      await api.post("mailService/markAsRead", {
        signature,
        uid: email.uid,
      });

      setEmails((prevEmails) =>
        prevEmails.map((e) =>
          e.uid === email.uid ? { ...e, isRead: true } : e
        )
      );
      setFilteredEmails((prevEmails) =>
        prevEmails.map((e) =>
          e.uid === email.uid ? { ...e, isRead: true } : e
        )
      );

      // Update the unread count
      setUnreadCount((prev) => prev - 1);
    } catch (error) {
      console.error("Error marking email as read:", error);
    }
  };

  const moveToBin = async (uids?: number[]) => {
    const emailsToMove = uids || selectedEmails;
    if (!signature || emailsToMove.length === 0) {
      showBasicAlert("Please select at least one email to move to bin.");
      return;
    }

    setIsLoading(true);
    try {
      const sourceFolder =
        activeTab === "inbox"
          ? "INBOX"
          : activeTab === "sent"
          ? "INBOX.Sent"
          : activeTab === "drafts"
          ? "INBOX.Drafts"
          : activeTab === "junk"
          ? "INBOX.Junk"
          : "";

      await api.post("mailService/moveMailToTrash", {
        signature,
        uids: emailsToMove,
        folder: sourceFolder,
      });
      showBasicAlert(
        `${emailsToMove.length} email(s) moved to bin successfully!`
      );
      setSelectedEmails([]);
      setShowSelectionMode(false);
      fetchEmails();
    } catch (error) {
      console.error("Error moving emails to bin:", error);
      showBasicAlert("Failed to move emails to bin. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      setIsLoadingLogout(true);
      await api.post("mailService/logoutIMAPController", {
        signature,
        provider,
      });
      window.location.reload();
    } catch (error) {
      console.error("Logout error:", error);
      showBasicAlert("Error during logout. Please try again.");
    }
  };

  return (
    <>
      {isLoadingLogout && <Loader />}
      <div className="gmail-container">
        {/* Sidebar */}
        <div className="gmail-sidebar">
          <button className="compose-btn" onClick={() => setShowCompose(true)}>
            <i className="ti ti-edit" /> Compose
          </button>
          <ul className="gmail-nav">
            {["inbox", "sent", "drafts", "junk", "trash"].map((tab) => (
              <li
                key={tab}
                className={activeTab === tab ? "active" : ""}
                onClick={() =>
                  handleTabChange(
                    tab as "inbox" | "sent" | "drafts" | "junk" | "trash"
                  )
                }
              >
                <i
                  className={`ti ${
                    tab === "inbox"
                      ? "ti-inbox"
                      : tab === "sent"
                      ? "ti-send"
                      : tab === "drafts"
                      ? "ti-file"
                      : tab === "junk"
                      ? "ti-alert-octagon"
                      : "ti-trash"
                  }`}
                />{" "}
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {tab === "inbox" && unreadCount > 0 && (
                  <span className="count">{unreadCount}</span>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Main Content */}
        <div className="gmail-content">
          {/* Toolbar */}
          <div className="gmail-toolbar">
            {/* Left placeholder for spacing if needed */}
            <div className="toolbar-left"></div>

            {/* Right Controls */}
            <div className="toolbar-right">
              {/* Search Button & Expanding Search Bar */}
              <div className={`search-container ${showSearch ? "open" : ""}`}>
                <input
                  type="text"
                  placeholder="Search mail"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                <button
                  className="icon-btn"
                  onClick={() => setShowSearch((prev) => !prev)}
                  title="Search"
                >
                  <i className="ti ti-search" />
                </button>
              </div>

              {/* Refresh Button */}
              <button
                className={`icon-btn ${isLoading ? "spinning" : ""}`}
                onClick={() => {
                  fetchEmails();
                }}
                title="Refresh"
              >
                <i className="ti ti-refresh" />
              </button>

              {/* Logout Button */}
              <button
                className="icon-btn logout-btn"
                onClick={handleLogout} // <-- Add your logout logic here
                title="Logout"
              >
                <i className="ti ti-logout" />
              </button>
            </div>
          </div>

          {/* Email list or detail */}
          {selectedEmail ? (
            <div className="slide-in">
              <EmailDetail
                email={selectedEmail}
                allEmails={[...emails, ...sentEmails]}
                onBack={() => setSelectedEmail(null)}
              />
            </div>
          ) : (
            <div className="gmail-email-list">
              {isLoading ? (
                <div className="loading-skeleton">
                  {[...Array(10)].map((_, i) => (
                    <div className="skeleton-email" key={i}>
                      <div className="skeleton-avatar"></div>
                      <div className="skeleton-lines">
                        <div className="skeleton-line short"></div>
                        <div className="skeleton-line"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                renderEmailList()
              )}
            </div>
          )}
        </div>
      </div>

      {/* Floating Compose Window */}
      {showCompose && (
        <div className="compose-popup fade-in">
          <ComposeEmail
            onSubmit={handleComposeSubmit}
            onCancel={() => setShowCompose(false)}
            isLoading={isLoading}
            replyToEmail={
              isReplying && selectedEmail
                ? convertToComposeReplyEmail(selectedEmail)
                : undefined
            }
            draftEmail={activeTab === "drafts" ? selectedEmail : undefined}
          />
        </div>
      )}
    </>
  );
};

export default HostingerMailView;
